import * as React from "react"
import {computed} from "@preact/signals-react";
import {useLocation} from "react-router-dom";
import {Box, Divider, Typography} from "@mui/material";
import {TooltipSeeker} from "@ui-seeker/core";

import {headerHeight, showHeaderCircle, showHeaderNotifications, showHeaderChat, showHeaderLang, appUsername, showUsername, navWidth, navWidthMini, pageTitle, pageSubtitle, pageActions, pageTitleAction} from "../../../@signals";
import {useApp} from "../../../@hooks";
import { dsColorNew } from "@palette";
import ToolbarButton from "./ToolbarButton";
import MaximalLayoutHeaderLangTool from "./LangTool";
import MaximalLayoutHeaderUserTool from "./UserTool";


const showDivider = computed(() => showHeaderCircle.value || showHeaderChat.value || showHeaderNotifications.value)

const dividerSx = { my: '12px', borderColor: dsColorNew.grayBorder }

// The testing environment says so next to every page title; the full sentence on hover
const IS_TESTING = window.location.host.includes('testing.')

function TestingBadge() {
    return <TooltipSeeker title="This is a testing environment. Please do not use for production purposes.">
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ml: 1.5,
            height: 26, px: 1.25, borderRadius: '999px', cursor: 'default',
            backgroundColor: dsColorNew.noteAmberBg, border: `1px solid ${dsColorNew.noteAmberBorder}`,
            color: dsColorNew.noteAmber, fontSize: 12, fontWeight: 700, letterSpacing: 0.6, lineHeight: 1,
        }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: dsColorNew.warningBar }}/>
            TESTING
        </Box>
    </TooltipSeeker>
}

export default function MaximalLayoutHeader({ headerTools=null, sx={}, links=[], ...props }) {
    const { isMini } = useApp()
    const { pathname } = useLocation()
    const leftOffset = isMini ? navWidthMini.value : navWidth.value
    const isHome = pathname.includes('/home')
    
    return <Box
        component="header"
        className="app-header"
        sx={{
            position: 'fixed',
            top: 0,
            left: leftOffset,
            width: `calc(100% - ${leftOffset}px)`,
            height: headerHeight.value,
            display: 'flex',
            alignItems: 'center',
            px: 4,
            backgroundColor: 'background.black',
            color: 'text.contrastText',
            boxShadow: 'none',
            zIndex: 1201,
            ...sx,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 1, mr: 2 }}>
            { pageTitle.value && <Box sx={{ width: 3, height: 20, borderRadius: '2px', backgroundColor: dsColorNew.webBlue500, flexShrink: 0 }} /> }

            { pageTitleAction.value }

            <Box sx={{ display: 'flex', minWidth: 0, flexDirection: { xs: 'column', xl: 'row' }, justifyContent: { xs: 'center', xl: 'flex-start' }, alignItems: { xl: 'baseline' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: 18, color: dsColorNew.textTitle, whiteSpace: 'nowrap', lineHeight: 1, mr: { xl: 1 } }}>
                    {pageTitle.value}
                </Typography>

                {pageSubtitle.value && <Typography sx={{ fontSize: { xs: 12, xl: 15 }, color: dsColorNew.appBlue, fontWeight: 500, whiteSpace: 'nowrap', lineHeight: 1, mt: { xs: 0.5, xl: 0 }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {pageSubtitle.value}
                </Typography>}
            </Box>

            { IS_TESTING && <TestingBadge/> }
        </Box>

        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto',
            // Every control in the header sits on the same 40px line, whatever the page puts there
            '& .MuiButton-root': { height: 40, whiteSpace: 'nowrap', flexShrink: 0 },
            '& .MuiInputBase-root': { height: 40, py: '0 !important', alignItems: 'center', flexWrap: 'nowrap' },
            '& .MuiInputBase-input': { py: '0 !important' },
            '& .MuiAutocomplete-root .MuiInputBase-root.MuiOutlinedInput-root': { pt: '0 !important', pb: '0 !important' },
        }}>
            { pageActions.value }

            { pageActions.value && <Divider orientation="vertical" flexItem sx={dividerSx} /> }

            { headerTools }

            { headerTools && <Divider orientation="vertical" flexItem sx={dividerSx} /> }


            { showHeaderChat.value && <ToolbarButton icon="comments" /> }
            { showHeaderNotifications.value && <ToolbarButton icon="bell" /> }

            { showDivider.value && <Divider orientation="vertical" flexItem sx={dividerSx} /> }

            { showHeaderLang.value && <MaximalLayoutHeaderLangTool/>}
            <MaximalLayoutHeaderUserTool links={links} />
        </Box>
    </Box>
}
